import { Box, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import { ProfessionalsSidebarDocument } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import { observer } from 'mobx-react'

import MonthPicker from 'modules/core/components/MonthPicker'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import companies from 'modules/core/store/collections/companies'
import { companyLegalEntities } from 'modules/core/store/collections/legalEntities'

import { useIncidenceMessageStats } from 'modules/bookkeepers/hooks/IncidenceMessageStatsContext'
import { useIncidenceStats } from 'modules/bookkeepers/hooks/IncidenceStatsContext'
import useGetPayrollResults from 'modules/bookkeepers/hooks/useGetPayrollResults'
import type { IFilters } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { setBookkeeperCompany, trackIncidenceFilter } from 'modules/bookkeepers/lib/amplitudeData'
import {
  accountMenuOptions,
  incidenceNavItemsWithCount,
  payrollNavItems,
  preferenceNavItems,
} from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import {
  getIncidenceStatsCount,
  getIncidencesUnreadMessagesCount,
} from 'modules/payroll_bundle/lib/Incidences/IncidenceFilters'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'

import AccountMenu from './AccountMenu'
import NavGroup from './NavGroup'
import ProfessionalsLogo from './professionals-logo.svg'

const DATE_FILTER_TYPE = 'date'
const LEGAL_ENTITY_ID_FILTER_TYPE = 'legal_entity_id'

const inhousePayrollFeatureId: AllFeatureIds = 'inhouse_payroll'
const factorialProfessionalsFeatureId: AllFeatureIds = 'factorial_professionals'
const Sidebar = () => {
  const { navigation } = useNavigation()
  const { email, access } = useCurrentSession()

  const incidenceStats = useIncidenceStats()
  const messageStats = useIncidenceMessageStats()
  const incidenceCountsStatuses = [INCIDENCE_STATUSES.to_do, INCIDENCE_STATUSES.doing].join(',')
  const incidenceCountsByType = getIncidenceStatsCount(
    incidenceStats,
    incidenceCountsStatuses
  ).get()
  const { incidencesUnreadMessagesCount } = getIncidencesUnreadMessagesCount(messageStats).get()
  const { payrollResults } = useGetPayrollResults()
  const payrollResultsCount = payrollResults.length

  const { filterDate, selectedCompany, onDateChange, onFilterChange, setSelectedCompany } =
    useIncidenceFilterContext()

  const bookkeeperContext = {
    isBookkeeperView: true,
  }
  const { data } = useQuery(ProfessionalsSidebarDocument, {
    variables: {
      companyId: selectedCompany?.id,
      featureAvailabilityIds: [Features.compensations_management],
      featureIds: [factorialProfessionalsFeatureId, inhousePayrollFeatureId],
    },
    skip: !selectedCompany,
    context: bookkeeperContext,
  })

  const featureAvailabilities = data?.companies.featureAvailabilitiesConnection.nodes || []

  const selectedCompanyHasCompensations = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.compensations_management
  )

  const handleDateChange = (value: string) => {
    onDateChange(value)
    trackFilter(DATE_FILTER_TYPE, value)
  }

  const handleCompanyChange = (companyId: string) => {
    const newSelectedCompany = companies.find({ id: +companyId }) ?? null
    const legalEntityIds =
      (newSelectedCompany ? companyLegalEntities(newSelectedCompany).map((le) => le.id) : []).join(
        ','
      ) ?? ''

    setSelectedCompany(newSelectedCompany)
    onFilterChange(LEGAL_ENTITY_ID_FILTER_TYPE, legalEntityIds)
  }

  const trackFilter = (filterType: keyof IFilters | string, value: string) => {
    trackIncidenceFilter(filterType, value, true, false)
  }

  // We use this to make sure that the company has a legal entity associated. If no
  // legal entity is fetched, it is because the company is not in Factorial Professionals.
  // Legal entities are required for the company or the filters won't work
  const companiesWithLegalEntity = companies.filter((model) => companyLegalEntities(model).length)

  const sortedCompanies = sortBy(companiesWithLegalEntity, (element) =>
    element.attributes.name.toUpperCase()
  )

  const companiesFilterOptions = () => {
    return [
      { value: '', label: i18n.t('bookkeepers.incidence.generic_filter.all_companies') },
      ...sortedCompanies.map((element) => ({
        value: element.id,
        label: element.attributes.name,
      })),
    ]
  }

  if (email) setBookkeeperCompany(email)

  return (
    <Box
      width="s300"
      background="grey1000"
      style={{ height: '100vh', position: 'sticky', top: '0' }}
      flexNoShrink
      paddingX="s24"
      paddingY="s24"
      gap="s24"
      overflowY="auto"
    >
      <Box width="full" alignItems="flexStart">
        <Box width="s200">
          <img src={ProfessionalsLogo} />
        </Box>
      </Box>
      <Box gap="s16">
        <Box minWidth="none">
          <Selector.Popover
            onChange={(companyId) => handleCompanyChange(companyId as string)}
            placeholder={i18n.t('bookkeepers.incidence.generic_filter.all_companies')}
            value={selectedCompany?.id ? [selectedCompany?.id] : []}
            blankLabel={i18n.t('common.empty')}
            searchPlaceholder={i18n.t('common.search')}
          >
            {companiesFilterOptions().map(({ label, value }) => (
              <Selector.Option label={label} searchValue={label} key={value} value={value}>
                <Box flexDirection="column">
                  <Text.Normal weight="medium">{label}</Text.Normal>
                </Box>
              </Selector.Option>
            ))}
          </Selector.Popover>
        </Box>
        <Box minWidth="none">
          <MonthPicker
            value={filterDate.value}
            placeholder={i18n.t('bookkeepers.incidence.generic_filter.all_dates')}
            onChange={(value: string) => handleDateChange(value)}
          />
        </Box>
      </Box>
      <Box height="full" justifyContent="spaceBetween">
        <Box gap="s24">
          <NavGroup
            title={i18n.t('bookkeepers.sidebar.incidences')}
            items={incidenceNavItemsWithCount(
              navigation,
              incidenceCountsByType,
              incidencesUnreadMessagesCount
            )}
          />

          {!selectedCompanyHasCompensations && (
            <NavGroup
              title={i18n.t('bookkeepers.sidebar.payroll')}
              items={payrollNavItems(navigation, payrollResultsCount)}
            />
          )}

          {selectedCompany && (
            <NavGroup
              title={i18n.t('bookkeepers.sidebar.preferences')}
              items={preferenceNavItems(navigation)}
            />
          )}
        </Box>
        <Box>
          <AccountMenu legalName={access.fullName} options={accountMenuOptions(navigation)} />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(Sidebar)
